import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useConnectedCrmIntegrations } from "@/hooks/useConnectedCrmIntegrations";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { globalFilterFn } from "@/utils/table-filters";
import { CompanyDTO } from "@shaarp/common";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "../data-table/data-table";
import { dataTableCompaniesColumns } from "./data-table-companies-columns";
import { DataTableCompaniesMobile } from "./data-table-companies-mobile";

interface DataTableCompaniesResponsiveProps {
  companies: CompanyDTO[];
  selected?: CompanyDTO;
  isLoading?: boolean;
  pagination?: boolean;
  showFilter?: boolean;
  additionalColumns?: any[];
  additionalFilters?: React.ReactNode;
  overrideColumnsVisibility?: string[];
  enabledListingFeature?: boolean;
  sortingState?: SortingState;
  onPaginationChange?: (pageIndex: number, pageSize: number) => void;
  rowClassNames?: (company: CompanyDTO) => string;
  onRowChanged?: (row: CompanyDTO) => void;
  toolbarContent?: React.ReactNode;
  columnsPersistenceKey?: string;
  emptyText?: string | null;
  onSortedDataChange?: (sortedCompanies: CompanyDTO[]) => void;
  showAssignedColumn?: boolean;
}

export function DataTableCompaniesResponsive({
  companies,
  selected,
  isLoading = false,
  pagination = true,
  showFilter = true,
  additionalColumns = [],
  additionalFilters = <></>,
  overrideColumnsVisibility = [],
  enabledListingFeature = true,
  sortingState = [{ id: "data-score", desc: true }],
  onPaginationChange = () => {},
  rowClassNames = () => "",
  onRowChanged = () => {},
  toolbarContent = null,
  columnsPersistenceKey = undefined,
  emptyText = null,
  onSortedDataChange,
  showAssignedColumn = true,
}: DataTableCompaniesResponsiveProps) {
  const { isMobile } = useDeviceFormat();
  const { t } = useTranslation();
  const { isAdminMode } = useAdminMode();
  const connectedCrmIntegrations = useConnectedCrmIntegrations();
  const [data, setData] = useState<CompanyDTO[]>([]);

  // Always call all hooks - for mobile card view table logic
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>(sortingState);

  // Use ref to avoid infinite loop with callback in dependencies
  const onSortedDataChangeRef = useRef(onSortedDataChange);
  useEffect(() => {
    onSortedDataChangeRef.current = onSortedDataChange;
  }, [onSortedDataChange]);

  useEffect(() => {
    setData(companies);
  }, [companies]);

  const handleRowChanged = (row: CompanyDTO) => {
    setData(
      companies.map((c) => {
        if (c.id === row.id) {
          return row;
        }
        return c;
      })
    );
    onRowChanged(row);
  };

  const showIntegrationsColumn = connectedCrmIntegrations.length > 0;
  const arePeopleVisible = useArePeopleVisible();

  const columns = useMemo(
    () =>
      dataTableCompaniesColumns(
        additionalColumns,
        overrideColumnsVisibility,
        handleRowChanged,
        t,
        showIntegrationsColumn,
        isAdminMode,
        showAssignedColumn,
        arePeopleVisible,
      ),
    [
      additionalColumns,
      overrideColumnsVisibility,
      handleRowChanged,
      t,
      showIntegrationsColumn,
      isAdminMode,
      showAssignedColumn,
      arePeopleVisible,
    ]
  );

  const columnsFinal = useMemo(
    () =>
      enabledListingFeature === true
        ? columns
        : columns.filter((col) => col.id !== "Listes"),
    [columns, enabledListingFeature]
  );

  const dataFinal = useMemo(() => [...data], [data]);

  const table = useReactTable({
    data: dataFinal,
    columns: columnsFinal,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: pagination ? 50 : 9999,
      },
    },
    autoResetPageIndex: false,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    globalFilterFn: globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // Notify parent of sorted/filtered data changes (for mobile mode)
  useEffect(() => {
    if (onSortedDataChangeRef.current && table) {
      const sortedRows = table.getSortedRowModel().rows;
      const sortedCompanies = sortedRows.map((row) => row.original);
      onSortedDataChangeRef.current(sortedCompanies);
    }
  }, [sorting, columnFilters, globalFilter, dataFinal, table]);

  if (isMobile) {
    return (
      <DataTableCompaniesMobile
        table={table}
        rowClassNames={rowClassNames}
        toolbarContent={toolbarContent}
        onPaginationChange={onPaginationChange}
        isLoading={isLoading}
        pagination={pagination}
        showFilter={showFilter}
        additionalFilters={additionalFilters}
        selected={selected}
      />
    );
  }

  return (
    <DataTable
      data={data}
      isLoadingStreaming={isLoading}
      columns={dataTableCompaniesColumns(
        additionalColumns,
        overrideColumnsVisibility,
        handleRowChanged,
        t,
        showIntegrationsColumn,
        isAdminMode,
        showAssignedColumn,
        arePeopleVisible,
      )}
      pagination={pagination}
      showFilter={showFilter}
      rowClassNames={rowClassNames}
      enabledListingFeature={enabledListingFeature}
      sortingState={sortingState}
      additionalFilters={additionalFilters}
      onPaginationChange={onPaginationChange}
      onSortedDataChange={onSortedDataChange}
      columnsPersistenceKey={columnsPersistenceKey}
      toolbarContent={toolbarContent}
      emptyText={emptyText}
      pageSize={pagination ? 50 : 999}
      stickyColumns={3}
    />
  );
}
